<div class="wrapper-replay-player">

  <!-- Header & Meta Bar -->
  <div class="section-title">
    <div class="header-content-wrap">
      <div class="header-left">
        <a [routerLink]="['/dashboard/replay', visitorId]" [queryParams]="backQueryParams" class="btn btn-back flex-shrink-0" [attr.aria-label]="'Back' | translate">
          <i class="fi-rr-arrow-alt-left"></i>
        </a>
        <div class="header-titles">
          <div class="d-flex align-items-center gap-2 flex-wrap">
            <span class="title" [attr.data-parent]="'Sessions' | translate">{{ "Session Replay" | translate }}</span>
            <span class="meta-pill font-monospace" (click)="copyInfo(sessionId, 'session')" [title]="'Click to copy Session ID' | translate">
              <i class="fi fi-rr-play-alt me-1"></i>
              <span>#{{ sessionId ? sessionId.split('-')[0] : '' }}</span>
              <i class="fi" [ngClass]="copiedNotice === 'session' ? 'fi-rr-check text-success ms-1' : 'fi-rr-copy text-muted ms-1'"></i>
            </span>
            <span class="meta-pill text-muted" (click)="copyInfo(visitorId, 'visitor')" [title]="'Click to copy Visitor ID' | translate">
              <i class="fi fi-rr-user me-1"></i>
              <span>{{ visitorId ? visitorId.split('-')[0] : '' }}</span>
              <i class="fi" [ngClass]="copiedNotice === 'visitor' ? 'fi-rr-check text-success ms-1' : 'fi-rr-copy text-muted ms-1'"></i>
            </span>
            <span class="badge bg-light text-dark border px-2 py-1" *ngIf="sessionViewport">
              <i class="fi me-1" [ngClass]="isMobileSession ? 'fi-rr-mobile' : (isTabletSession ? 'fi-rr-tablet' : 'fi-rr-laptop')"></i>
              {{ sessionViewport.width }} × {{ sessionViewport.height }}
            </span>
            <div class="btn-group speed-segmented-group" role="group" *ngIf="visitPageCount > 1"
                 [title]="'Play all pages of this visit one after another, or only this page' | translate">
              <button type="button" class="btn btn-xs speed-btn" [class.active]="visitMode"
                      [disabled]="isLoading" (click)="setVisitMode(true)">
                <i class="fi fi-rr-globe me-1"></i>{{ "Whole visit" | translate }} ({{ visitPageCount }} {{ "pages" | translate }})
              </button>
              <button type="button" class="btn btn-xs speed-btn" [class.active]="!visitMode"
                      [disabled]="isLoading" (click)="setVisitMode(false)">
                {{ "This page" | translate }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="header-right">
        <div class="d-flex align-items-center gap-2">
          <!-- AI Summary & Insights Button -->
          <span
            class="ai-btn-wrapper"
            [class.is-disabled]="isAiSummaryDisabled"
            [title]="aiSummaryTooltip | translate"
          >
            <button
              type="button"
              class="btn btn-sm btn-action-ai d-flex align-items-center gap-1"
              data-ai-purpose="session replay summary"
              [disabled]="isAiSummaryDisabled"
              (click)="openAiSummary($event)"
              [title]="aiSummaryTooltip | translate"
            >
              <i class="fi fi-rr-sparkles" [class.fa-spin]="isGeneratingAiSummary"></i>
              <span>{{ (isGeneratingAiSummary ? "Analyzing..." : "AI Summary & Insights") | translate }}</span>
              <span class="ai-coin-token" *ngIf="!isGeneratingAiSummary && aiTokenCost" [attr.title]="aiTokenCost + ' tokens'">
                {{ aiTokenCost }}
              </span>
            </button>
          </span>

          <!-- Toggle Activity Feed -->
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
            [class.active]="showActivityDrawer"
            (click)="toggleActivityDrawer()"
          >
            <i class="fi fi-rr-list-check"></i>
            <span>{{ "Events Log" | translate }}</span>
            <span class="badge bg-primary text-white rounded-pill ms-1" *ngIf="timelineEvents.length">{{ timelineEvents.length }}</span>
          </button>

          <!-- Shortcuts Help Button -->
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary btn-icon-only d-none d-md-inline-flex"
            (click)="showShortcutsModal = !showShortcutsModal"
            title="Keyboard Shortcuts (?)"
          >
            <i class="fi fi-rr-keyboard"></i>
          </button>
        </div>
      </div>
    </div>
  </div>

  <div class="mt-3">
    <!-- Loading State -->
    <div *ngIf="isLoading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status"></div>
      <p class="mt-3 text-muted">{{ "Loading session and reconstructing DOM events..." | translate }}</p>
    </div>

    <!-- Error State -->
    <div *ngIf="!isLoading && error" class="text-center py-5">
      <div class="empty-icon-circle mx-auto mb-3">
        <i class="fi fi-rr-exclamation" style="font-size: 2rem; color: #ef4444;"></i>
      </div>
      <h6 class="text-danger font-weight-bold">{{ error }}</h6>
      <button class="btn btn-sm btn-outline-primary mt-2" (click)="loadSession()">
        <i class="fi fi-rr-refresh me-1"></i> {{ "Retry Loading" | translate }}
      </button>
    </div>

    <!-- Player Card Wrapper -->
    <div *ngIf="!isLoading && !error && events.length > 0" class="player-card" [class.is-fullscreen]="isFullscreen" [class.has-drawer]="showActivityDrawer">

      <!-- Player Header URL Bar -->
      <div class="player-top-header d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
        <div class="d-flex align-items-center gap-2 text-truncate me-2">
          <span class="pulse-live-dot" [class.playing]="playing"></span>
          <span class="text-muted small font-weight-bold">{{ "URL:" | translate }}</span>
          <span class="session-url-text font-monospace small text-truncate" [title]="sessionUrl">
            {{ sessionUrl || 'https://' }}
          </span>
        </div>
        <div class="d-flex align-items-center gap-2 flex-shrink-0">
          <span class="text-muted small">
            {{ formattedCurrent }} / {{ formattedTotal }}
          </span>
        </div>
      </div>

      <!-- Main Central Area (Canvas + Side Activity Drawer) -->
      <div class="player-main-layout">

        <!-- rrweb Canvas Screen -->
        <div class="player-area" [class.is-desktop]="!isMobileSession && !isTabletSession">
          <div *ngIf="!playerReady" class="player-initializing">
            <div class="spinner-border text-primary" role="status"></div>
            <p class="mt-3 text-muted small">{{ "Preparing replay..." | translate }}</p>
          </div>

          <!-- Mobile session: phone frame -->
          <ng-container *ngIf="isMobileSession; else tabletOrDesktop">
            <div class="phone-frame" [class.invisible]="!playerReady">
              <div class="phone-frame__top-bar">
                <div class="phone-frame__camera"></div>
              </div>
              <div class="phone-frame__screen">
                <div class="player-scale-clip">
                  <div id="rr-player-root"></div>
                </div>
              </div>
              <div class="phone-frame__bottom-bar">
                <div class="phone-frame__home-indicator"></div>
              </div>
            </div>
          </ng-container>

          <!-- Tablet session: tablet frame -->
          <ng-template #tabletOrDesktop>
            <ng-container *ngIf="isTabletSession; else desktopPlayer">
              <div class="tablet-frame" [class.invisible]="!playerReady" [class.is-landscape]="isTabletLandscape">
                <div class="tablet-frame__top-bar">
                  <div class="tablet-frame__camera"></div>
                </div>
                <div class="tablet-frame__screen">
                  <div class="player-scale-clip">
                    <div id="rr-player-root"></div>
                  </div>
                </div>
              </div>
            </ng-container>
          </ng-template>

          <!-- Desktop session: browser frame -->
          <ng-template #desktopPlayer>
            <div class="browser-frame" [class.invisible]="!playerReady">
              <div class="browser-frame__tab-bar">
                <div class="browser-frame__tab">
                  <i class="fi fi-rr-globe browser-frame__favicon"></i>
                  <span class="browser-frame__tab-title">{{ sessionUrl || 'Session Replay' }}</span>
                </div>
                <span class="browser-frame__viewport-badge" *ngIf="sessionViewport">
                  {{ sessionViewport.width }} × {{ sessionViewport.height }}
                </span>
              </div>
              <div class="browser-frame__screen">
                <div class="player-scale-clip">
                  <div id="rr-player-root"></div>
                </div>
              </div>
            </div>
          </ng-template>
        </div>

        <!-- Side Activity & Event Log Drawer -->
        <div class="activity-drawer" *ngIf="showActivityDrawer">
          <div class="activity-drawer__header">
            <div class="d-flex align-items-center justify-content-between mb-2">
              <span class="drawer-title font-weight-bold">
                <i class="fi fi-rr-bolt me-1 text-primary"></i>
                {{ "Activity Feed" | translate }}
              </span>
              <button class="btn btn-xs btn-outline-secondary" (click)="toggleActivityDrawer()">
                <i class="fi fi-rr-cross-small"></i>
              </button>
            </div>

            <!-- Activity Search -->
            <div class="activity-search-wrap mb-2">
              <input
                type="text"
                class="form-control form-control-sm"
                [(ngModel)]="activitySearchQuery"
                [placeholder]="'Filter events...' | translate"
              />
            </div>

            <!-- Activity Type Filter Pills -->
            <div class="d-flex align-items-center gap-1 flex-wrap">
              <button
                class="btn btn-xs filter-chip"
                [class.active]="activeActivityFilter === 'all'"
                (click)="activeActivityFilter = 'all'"
              >
                {{ "All" | translate }} ({{ timelineEvents.length }})
              </button>
              <button
                class="btn btn-xs filter-chip"
                [class.active]="activeActivityFilter === 'clicks'"
                (click)="activeActivityFilter = 'clicks'"
              >
                {{ "Clicks" | translate }}
              </button>
              <button
                class="btn btn-xs filter-chip"
                [class.active]="activeActivityFilter === 'inputs'"
                (click)="activeActivityFilter = 'inputs'"
              >
                {{ "Inputs" | translate }}
              </button>
              <button
                class="btn btn-xs filter-chip"
                [class.active]="activeActivityFilter === 'navs'"
                (click)="activeActivityFilter = 'navs'"
              >
                {{ "Pages" | translate }}
              </button>
            </div>
          </div>

          <!-- Activity List Items -->
          <div class="activity-drawer__body">
            <div *ngIf="filteredActivityEvents.length === 0" class="text-center py-4 text-muted small">
              {{ "No activity events matching filter." | translate }}
            </div>

            <div
              class="activity-item"
              *ngFor="let ev of filteredActivityEvents"
              [class.active]="currentActiveEventId === ev.id"
              (click)="seekTo(ev.timeMs)"
              [title]="ev.label + ' • ' + formatTime(ev.timeMs) + (ev.subLabel ? ' (' + ev.subLabel + ')' : '')"
            >
              <div class="activity-time font-monospace">{{ formatTime(ev.timeMs) }}</div>
              <div class="activity-badge-icon" [ngClass]="ev.badgeClass">
                <i class="fi" [ngClass]="ev.icon"></i>
              </div>
              <div class="activity-content">
                <div class="activity-label text-truncate">{{ ev.label }}</div>
                <div class="activity-sub text-muted small text-truncate" *ngIf="ev.subLabel">{{ ev.subLabel }}</div>
              </div>
            </div>
          </div>
        </div>

      </div>

      <!-- Rich Interactive Scrubber Bar -->
      <div class="progress-wrap">
        <div
          class="progress-bar-click-area"
          (click)="onProgressClick($event)"
          (mousemove)="onScrubberMouseMove($event)"
          (mouseleave)="onScrubberMouseLeave()"
        >
          <div class="progress-bar-track">

            <!-- Inactivity Gap Zones -->
            <div
              class="inactivity-gap-zone"
              *ngFor="let gap of gapIntervals"
              [style.left.%]="gap.percentStart"
              [style.width.%]="gap.percentWidth"
              title="Skipped Inactivity Zone"
            ></div>

            <!-- Event Heat Markers / Pins -->
            <div
              class="timeline-marker-pin"
              *ngFor="let ev of timelineEvents"
              [style.left.%]="ev.percent"
              [ngClass]="ev.badgeClass"
              [title]="formatTime(ev.timeMs) + ' • ' + ev.label"
              (click)="seekTo(ev.timeMs); $event.stopPropagation()"
            ></div>

            <!-- Progress Fill -->
            <div class="progress-bar-fill" [style.transform]="'scaleX(' + (progressPct / 100) + ')'"></div>

            <!-- Scrubber Playhead Handle -->
            <div class="progress-bar-handle" [style.left.%]="progressPct"></div>

            <!-- Hover Preview Line & Tooltip -->
            <div class="hover-preview-line" *ngIf="isHoveringScrubber" [style.left.%]="hoverPercent">
              <div class="hover-tooltip-box" [style.transform]="hoverTooltipTransform">
                <span class="hover-time font-monospace">{{ hoverFormattedTime }}</span>
                <span class="hover-label text-truncate" *ngIf="hoverEventLabel">{{ hoverEventLabel }}</span>
              </div>
            </div>

          </div>
        </div>
      </div>

      <!-- Modern Controls Bar -->
      <div class="controls-bar">
        <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">

          <!-- Left Transport Controls -->
          <div class="d-flex align-items-center gap-2">
            <button class="btn btn-control btn-control-secondary" (click)="rewind(10000)" title="Rewind 10s (← / J)">
              <i class="fi fi-rr-angle-double-left"></i>
              <span class="control-label">10s</span>
            </button>

            <button class="btn btn-control btn-control-primary" (click)="playing ? pause() : play()" [title]="playing ? 'Pause (Space / K)' : 'Play (Space / K)'">
              <i [class]="playing ? 'fi fi-rr-pause' : 'fi fi-rr-play'"></i>
              <span class="control-label font-weight-bold">{{ (playing ? "Pause" : "Play") | translate }}</span>
            </button>

            <button class="btn btn-control btn-control-secondary" (click)="forward(10000)" title="Forward 10s (→ / L)">
              <span class="control-label">10s</span>
              <i class="fi fi-rr-angle-double-right"></i>
            </button>

            <button class="btn btn-control btn-control-subtle" (click)="restartAndPlay()" title="Restart from beginning">
              <i class="fi fi-rr-refresh"></i>
            </button>
          </div>

          <!-- Center: Speed Selector -->
          <div class="d-flex align-items-center gap-2">
            <span class="control-meta-label text-muted small">{{ "Speed:" | translate }}</span>
            <div class="btn-group speed-segmented-group" role="group">
              <button
                type="button"
                class="btn btn-xs speed-btn"
                *ngFor="let s of [0.5, 1, 2, 4]"
                [class.active]="speed === s"
                (click)="setSpeed(s)"
              >
                {{ s }}×
              </button>
            </div>
          </div>

          <!-- Right: Skip Inactivity Switch & Fullscreen -->
          <div class="d-flex align-items-center gap-3">
            <div class="custom-switch-wrapper d-flex align-items-center gap-2" [title]="'Automatically fast-forward long idle periods' | translate">
              <input
                type="checkbox"
                id="skipInactiveToggle"
                class="custom-switch-input"
                [ngModel]="skipInactive"
                (ngModelChange)="changeSkipInactive($event)"
              />
              <label for="skipInactiveToggle" class="custom-switch-label mb-0 small text-muted">
                {{ "Skip inactive" | translate }}
                <span class="badge bg-light text-muted border rounded-pill ms-1" *ngIf="gapIntervals.length">
                  {{ gapIntervals.length }} {{ "gaps" | translate }}
                </span>
              </label>
            </div>

            <button
              class="btn btn-control btn-control-subtle"
              (click)="isFullscreen ? closeFullscreen() : openFullscreen()"
              [title]="'Toggle Fullscreen (F)' | translate"
            >
              <i [class]="isFullscreen ? 'fi fi-rr-compress' : 'fi fi-rr-expand'"></i>
            </button>
          </div>

        </div>
      </div>

    </div>
  </div>

  <!-- Keyboard Shortcuts Modal / Popover -->
  <div class="shortcuts-modal-overlay" *ngIf="showShortcutsModal" (click)="showShortcutsModal = false">
    <div class="shortcuts-modal-card" (click)="$event.stopPropagation()">
      <div class="d-flex align-items-center justify-content-between mb-3 border-bottom pb-2">
        <h6 class="mb-0 font-weight-bold">
          <i class="fi fi-rr-keyboard me-2 text-primary"></i>
          {{ "Keyboard Shortcuts" | translate }}
        </h6>
        <button class="btn btn-xs btn-outline-secondary" (click)="showShortcutsModal = false">
          <i class="fi fi-rr-cross-small"></i>
        </button>
      </div>
      <div class="shortcuts-list">
        <div class="shortcut-row d-flex justify-content-between py-1">
          <span class="text-muted">{{ "Play / Pause" | translate }}</span>
          <kbd>Space</kbd> or <kbd>K</kbd>
        </div>
        <div class="shortcut-row d-flex justify-content-between py-1">
          <span class="text-muted">{{ "Rewind 10 seconds" | translate }}</span>
          <kbd>←</kbd> or <kbd>J</kbd>
        </div>
        <div class="shortcut-row d-flex justify-content-between py-1">
          <span class="text-muted">{{ "Forward 10 seconds" | translate }}</span>
          <kbd>→</kbd> or <kbd>L</kbd>
        </div>
        <div class="shortcut-row d-flex justify-content-between py-1">
          <span class="text-muted">{{ "Toggle Fullscreen" | translate }}</span>
          <kbd>F</kbd>
        </div>
        <div class="shortcut-row d-flex justify-content-between py-1">
          <span class="text-muted">{{ "Toggle Skip Inactive" | translate }}</span>
          <kbd>M</kbd>
        </div>
        <div class="shortcut-row d-flex justify-content-between py-1">
          <span class="text-muted">{{ "Playback Speed (1x, 2x, 4x, 0.5x)" | translate }}</span>
          <span><kbd>1</kbd> <kbd>2</kbd> <kbd>3</kbd> <kbd>4</kbd></span>
        </div>
      </div>
    </div>
  </div>

  <!-- AI Recording Summary & Insights Modal -->
  <div class="ai-summary-modal-overlay" *ngIf="showAiSummaryModal" (click)="closeAiSummary()">
    <div class="ai-summary-modal-card" (click)="$event.stopPropagation()">
      <!-- Modal Header -->
      <div class="ai-modal-header d-flex align-items-center justify-content-between pb-3 border-bottom">
        <div class="d-flex align-items-center gap-2">
          <div class="ai-icon-badge">
            <i class="fi fi-rr-sparkles"></i>
          </div>
          <div>
            <div class="ai-title font-weight-bold d-flex align-items-center gap-2">
              <span>{{ "AI Recording Summary & Insights" | translate }}</span>
              <span class="badge bg-purple-subtle text-purple border rounded-pill small" *ngIf="sessionMetrics">
                {{ sessionMetrics.frictionLevel }}
              </span>
            </div>
            <div class="ai-subtitle text-muted small">
              {{ "Automated behavioral analysis, user journey breakdown, and UX optimization insights" | translate }}
            </div>
          </div>
        </div>

        <div class="ai-modal-actions d-flex align-items-center gap-2">
          <button
            *ngIf="!isGeneratingAiSummary"
            type="button"
            class="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
            data-ai-purpose="session replay summary"
            (click)="generateAiSummary($event)"
            [title]="'Regenerate Summary' | translate"
          >
            <i class="fi fi-rr-refresh"></i>
            <span>{{ "Regenerate" | translate }}</span>
            <span class="ai-coin-token ai-coin-sm" *ngIf="aiTokenCost" [attr.title]="aiTokenCost + ' tokens'">
              {{ aiTokenCost }}
            </span>
          </button>

          <button
            type="button"
            class="btn btn-sm btn-icon-close"
            (click)="closeAiSummary()"
            [attr.aria-label]="'Close' | translate"
          >
            <i class="fi fi-rr-cross"></i>
          </button>
        </div>
      </div>

      <!-- Quick Session KPI Metric Chips -->
      <div class="ai-modal-kpi-bar py-3 border-bottom" *ngIf="sessionMetrics">
        <div class="row g-2">
          <div class="col-6 col-md-3">
            <div class="kpi-mini-pill">
              <span class="kpi-mini-icon text-primary"><i class="fi fi-rr-clock"></i></span>
              <div>
                <div class="kpi-mini-label">{{ "Duration" | translate }}</div>
                <div class="kpi-mini-val font-monospace">{{ sessionMetrics.formattedDuration }}</div>
              </div>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="kpi-mini-pill">
              <span class="kpi-mini-icon text-success"><i class="fi fi-rr-bolt"></i></span>
              <div>
                <div class="kpi-mini-label">{{ "Active Engagement" | translate }}</div>
                <div class="kpi-mini-val">{{ sessionMetrics.activePercent }}% <span class="small text-muted font-weight-normal">({{ sessionMetrics.formattedActiveTime }})</span></div>
              </div>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="kpi-mini-pill">
              <span class="kpi-mini-icon text-purple"><i class="fi fi-rr-mouse"></i></span>
              <div>
                <div class="kpi-mini-label">{{ "Interactions" | translate }}</div>
                <div class="kpi-mini-val">{{ sessionMetrics.totalInteractions }} <span class="small text-muted font-weight-normal">({{ sessionMetrics.clickCount }} clicks, {{ sessionMetrics.inputCount }} inputs)</span></div>
              </div>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="kpi-mini-pill" [class.friction-alert]="sessionMetrics.rageClickCount > 0">
              <span class="kpi-mini-icon" [ngClass]="sessionMetrics.rageClickCount > 0 ? 'text-danger' : 'text-muted'"><i class="fi fi-rr-exclamation"></i></span>
              <div>
                <div class="kpi-mini-label">{{ "Frustration Signals" | translate }}</div>
                <div class="kpi-mini-val" [class.text-danger]="sessionMetrics.rageClickCount > 0">
                  {{ sessionMetrics.rageClickCount }} {{ "Rage Clicks" | translate }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal Body Content -->
      <div class="ai-modal-body py-3">

        <!-- Loading State -->
        <div *ngIf="isGeneratingAiSummary" class="ai-loading-container py-5 text-center">
          <div class="ai-spinner mb-3">
            <i class="fi fi-rr-sparkles fa-spin"></i>
          </div>
          <div class="font-weight-bold text-primary mb-1">
            {{ "Analyzing user session recording & reconstructing journey..." | translate }}
          </div>
          <div class="text-muted small">
            {{ "Evaluating interaction patterns, click sequences, hesitation points, and conversion signals" | translate }}
          </div>
          <div class="ai-skeleton-box mt-3 mx-auto" style="max-width: 500px;">
            <div class="skeleton-line full"></div>
            <div class="skeleton-line three-quarters"></div>
            <div class="skeleton-line half"></div>
          </div>
        </div>

        <!-- Error State -->
        <div *ngIf="!isGeneratingAiSummary && aiSummaryError" class="ai-error-container p-3 my-2">
          <div class="d-flex align-items-center text-danger mb-2">
            <i class="fi fi-rr-exclamation mr-2"></i>
            <span class="font-weight-bold">{{ aiSummaryError }}</span>
          </div>
          <button type="button" class="btn btn-sm btn-primary mt-1" data-ai-purpose="session replay summary" (click)="generateAiSummary($event)">
            {{ "Try Again" | translate }}
          </button>
        </div>

        <!-- AI Content Display -->
        <div *ngIf="!isGeneratingAiSummary && !aiSummaryError && aiSummaryContent" class="ai-summary-content-wrap">
          <!-- Structured Insights Cards Grid -->
          <div class="ai-insights-grid" *ngIf="parsedSections && parsedSections.length > 0">
            <div
              *ngFor="let sec of parsedSections"
              class="ai-insight-card"
              [ngClass]="'theme-' + sec.theme"
            >
              <div class="ai-card-header d-flex align-items-center gap-2">
                <span class="ai-card-badge">
                  <i class="fi" [ngClass]="sec.icon"></i>
                </span>
                <h6 class="ai-card-title mb-0 font-weight-bold">{{ sec.title | translate }}</h6>
              </div>

              <div class="ai-card-body mt-2">
                <div *ngFor="let row of sec.rows" class="ai-row-item">
                  <span class="item-bullet"></span>
                  <div class="item-content">
                    <span *ngIf="row.label" class="ai-label font-weight-bold">{{ row.label }}: </span>
                    <ng-container *ngFor="let tok of getInsightTokens((row.text || row.value || '') + (row.note ? ' - ' + row.note : ''))">
                      <strong *ngIf="tok.isBold">{{ tok.text }}</strong>
                      <button
                        *ngIf="tok.isTimestamp"
                        type="button"
                        class="badge-timestamp-inline"
                        (click)="jumpToTimestamp(tok.ms); $event.stopPropagation()"
                        [title]="'Jump replay to ' + tok.text"
                      >
                        {{ tok.text }}
                      </button>
                      <span *ngIf="!tok.isBold && !tok.isTimestamp">{{ tok.text }}</span>
                    </ng-container>
                    <a
                      *ngIf="row.url"
                      [href]="row.url"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="ai-url-pill ms-1"
                      [attr.title]="row.url"
                    >
                      <i class="fi fi-rr-arrow-square-right me-1"></i>
                      <span>{{ displayUrl(row.url) }}</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Raw Fallback if no sections were parsed -->
          <div *ngIf="!parsedSections || parsedSections.length === 0" class="ai-fallback-text p-3">
            <div class="small text-muted" style="white-space: pre-wrap;">{{ aiSummaryContent }}</div>
          </div>
        </div>

      </div>

      <!-- Modal Footer -->
      <div class="ai-modal-footer pt-3 pb-3 border-top d-flex flex-column align-items-center gap-2">
        <!-- Feedback form (only for a generated summary) -->
        <div
          *ngIf="showFeedbackForm && !feedbackSubmitted && aiSummaryContent && !isGeneratingAiSummary"
          class="ai-feedback-form"
        >
          <div class="ai-feedback-question">{{ "Was this summary helpful?" | translate }}</div>
          <div class="ai-feedback-rating d-flex justify-content-center gap-2">
            <button
              type="button"
              class="btn btn-sm ai-feedback-choice"
              [class.selected]="feedbackRating === 'helpful'"
              [attr.aria-pressed]="feedbackRating === 'helpful'"
              (click)="feedbackRating = 'helpful'"
            >
              <i class="fi fi-rr-smile"></i>
              <span>{{ "Helpful" | translate }}</span>
            </button>
            <button
              type="button"
              class="btn btn-sm ai-feedback-choice"
              [class.selected]="feedbackRating === 'not_helpful'"
              [attr.aria-pressed]="feedbackRating === 'not_helpful'"
              (click)="feedbackRating = 'not_helpful'"
            >
              <i class="fi fi-rr-frown"></i>
              <span>{{ "Not helpful" | translate }}</span>
            </button>
          </div>
          <textarea
            class="form-control form-control-sm ai-feedback-comment"
            rows="3"
            maxlength="800"
            [(ngModel)]="feedbackComment"
            [placeholder]="'Tell us what was accurate, wrong or missing (optional)' | translate"
          ></textarea>
          <div *ngIf="feedbackError" class="ai-feedback-error small">{{ feedbackError }}</div>
          <div class="d-flex justify-content-end gap-2">
            <button type="button" class="btn btn-sm btn-light" (click)="cancelAiFeedback()" [disabled]="isSubmittingFeedback">
              {{ "Cancel" | translate }}
            </button>
            <button
              type="button"
              class="btn btn-sm btn-primary"
              (click)="submitAiFeedback()"
              [disabled]="!feedbackRating || isSubmittingFeedback"
            >
              {{ (isSubmittingFeedback ? "Sending..." : "Send feedback") | translate }}
            </button>
          </div>
        </div>

        <div *ngIf="feedbackSubmitted" class="ai-feedback-thanks small text-center">
          <i class="fi fi-rr-comment-check me-1"></i>
          {{ "Thank you! Your feedback helps us improve our AI session insights." | translate }}
        </div>

        <div class="ai-disclaimer-note text-muted small text-center" *ngIf="!showFeedbackForm && !feedbackSubmitted">
          <span>{{ "Your insights are powered by AI, so mistakes are possible." | translate }}</span>
          <ng-container *ngIf="aiSummaryContent && !isGeneratingAiSummary">
            <a href="javascript:void(0)" class="feedback-link ms-1 font-weight-bold" (click)="shareFeedback()">
              {{ "Share feedback" | translate }}
            </a>
            <span> {{ "so we can improve!" | translate }}</span>
          </ng-container>
        </div>
        <button type="button" class="btn btn-sm btn-secondary px-4" (click)="closeAiSummary()">
          {{ "Close" | translate }}
        </button>
      </div>

    </div>
  </div>

</div>
